/**
 * parseCapabilityURN — pure helper for Jobe data-capability URN parsing.
 *
 * Handles two forms:
 *
 *   6-segment canonical:  urn:jobe:data-capability:{ns}:{domain}:{action}
 *   Other / fallback:     join last two segments with " · ", or full URN
 *
 * 5-segment compact URNs are no longer published by any live producer
 * after JOBE-0153 (catalog.items.search, conv.threads.list/get,
 * scm.files.read, scm.code.search). The parser no longer special-cases
 * them — a stray 5-seg URN falls through to the generic 2-segment
 * fallback path.
 *
 * Title-casing rule: split each segment on both `_` and `-`, title-case
 * each token, join with space.
 *   "pull_requests"   → "Pull Requests"
 *   "mint_push_token" → "Mint Push Token"
 *   "accounts"        → "Accounts"
 *
 * Returns { domain, action, label, namespace } where:
 *   domain    — domain string (e.g. "transactions") or null for fallback
 *   action    — action string (e.g. "list") or null for fallback
 *   label     — display label suitable for rendering ("Transactions · List")
 *   namespace — namespace segment or null for fallback
 */

/**
 * Title-case a single URN segment: split on `_` and `-`, capitalise each
 * token, join with space.
 *
 * @param {string} segment
 * @returns {string}
 */
function titleCaseSegment(segment) {
  return segment
    .split(/[_-]/)
    .filter(Boolean)
    .map((token) => token.charAt(0).toUpperCase() + token.slice(1))
    .join(' ');
}

/**
 * domainLabel returns the title-cased display label for a domain segment.
 * This is the canonical single path for deriving a domain's display name;
 * all consumers (CapabilitiesTab, AccessTab, ActTab) should use this rather
 * than re-deriving from _parsed.label.
 *
 * @param {string} domain  — raw domain string (e.g. "pull-requests", "transactions")
 * @returns {string}       — title-cased label (e.g. "Pull Requests", "Transactions")
 */
export function domainLabel(domain) {
  return titleCaseSegment(domain);
}

/**
 * parseCapabilityURN parses a Jobe data-capability URN into its component
 * parts and a display label.
 *
 * @param {string} urn
 * @returns {{ domain: string|null, action: string|null, label: string, namespace: string|null }}
 */
export function parseCapabilityURN(urn) {
  if (!urn || typeof urn !== 'string') {
    return { domain: null, action: null, label: typeof urn === 'string' ? urn : '', namespace: null };
  }

  const parts = urn.split(':');

  // 6-segment canonical: urn:jobe:data-capability:{ns}:{domain}:{action}
  if (parts.length === 6 && parts[0] === 'urn' && parts[1] === 'jobe' && parts[2] === 'data-capability') {
    const namespace = parts[3];
    const domain = parts[4];
    const action = parts[5];
    return {
      domain,
      action,
      label: `${titleCaseSegment(domain)} · ${titleCaseSegment(action)}`,
      namespace,
    };
  }

  // Fallback: join last two segments with " · ", or return full URN when
  // fewer than two segments exist. Covers historical / non-canonical URNs
  // including any stray 5-seg compact form.
  if (parts.length >= 2) {
    const last = parts[parts.length - 1];
    const secondLast = parts[parts.length - 2];
    return {
      domain: null,
      action: null,
      label: `${titleCaseSegment(secondLast)} · ${titleCaseSegment(last)}`,
      namespace: null,
    };
  }

  return { domain: null, action: null, label: urn, namespace: null };
}

/**
 * groupCapabilitiesByDomain groups a list of capability objects (with an
 * `id` URN field) into domain groups for rendering.
 *
 * Group order: alphabetical by domain. Capabilities whose URN yields no
 * domain (fallback) form a flat list keyed to their providerRef, sorted
 * after the alphabetised domain groups.
 *
 * Within each group, capabilities are sorted alphabetically by action.
 *
 * Each capability object in the returned groups is augmented with a
 * `_parsed` field containing `{ domain, action, label, namespace }` as
 * returned by `parseCapabilityURN`. This is part of the public contract so
 * consumers (AccessTab, ActTab, etc.) can rely on `cap._parsed` without
 * re-parsing. Do not strip `_parsed` from the returned objects.
 *
 * Each non-fallback group's `label` is the canonical title-cased domain
 * display string produced by `domainLabel(domain)`. Consumers MUST read
 * `group.label` directly — do not re-derive from `cap._parsed.label`.
 *
 * @param {Array<{ id: string, description?: string, sensitivity?: string, providerRef?: string }>} caps
 * @returns {Array<{ domain: string, label: string, capabilities: Array<object & { _parsed: { domain: string|null, action: string|null, label: string, namespace: string|null } }>, isFallback: boolean }>}
 */
export function groupCapabilitiesByDomain(caps) {
  if (!caps || caps.length === 0) return [];

  const domainGroups = new Map(); // domain string → [cap, ...]
  const fallbackGroups = new Map(); // providerRef → [cap, ...]

  for (const cap of caps) {
    const parsed = parseCapabilityURN(cap.id);
    const augmented = { ...cap, _parsed: parsed };

    if (parsed.domain) {
      if (!domainGroups.has(parsed.domain)) domainGroups.set(parsed.domain, []);
      domainGroups.get(parsed.domain).push(augmented);
    } else {
      const key = (typeof cap.providerRef === 'string' && cap.providerRef) || '(unknown provider)';
      if (!fallbackGroups.has(key)) fallbackGroups.set(key, []);
      fallbackGroups.get(key).push(augmented);
    }
  }

  // Sort domain groups alphabetically; sort within each group by action.
  const sortedDomains = Array.from(domainGroups.keys()).sort();
  const result = sortedDomains.map((domain) => {
    const groupCaps = domainGroups.get(domain).sort((a, b) => {
      const aAction = a._parsed.action || '';
      const bAction = b._parsed.action || '';
      return aAction.localeCompare(bAction);
    });
    return {
      domain,
      label: domainLabel(domain),
      capabilities: groupCaps,
      isFallback: false,
    };
  });

  // Fallback groups appended after alphabetised domain groups.
  for (const [key, fbCaps] of fallbackGroups) {
    result.push({
      domain: key,
      label: key,
      capabilities: fbCaps.sort((a, b) => (a._parsed.label || '').localeCompare(b._parsed.label || '')),
      isFallback: true,
    });
  }

  return result;
}

/**
 * sensitivityOrder returns an integer priority for a sensitivity string,
 * with lower numbers being higher severity.
 *
 * critical=0, high=1, normal=2, low=3
 */
export function sensitivityOrder(s) {
  const order = { critical: 0, high: 1, normal: 2, low: 3 };
  return s in order ? order[s] : 4;
}

/**
 * buildSummaryLine computes the sensitivity summary string for the
 * capabilities tab header, following the three rendering modes:
 *
 * 1. Uniform: "N capabilities · all {sensitivity}"
 * 2. Mixed with elevated: "N capabilities · X high · Y low" (descending severity)
 * 3. Omitted (null): no high/critical and no low — nothing to summarise.
 *
 * @param {Array<{ sensitivity?: string }>} caps
 * @returns {string|null}
 */
export function buildSummaryLine(caps) {
  if (!caps || caps.length === 0) return null;

  const counts = {};
  for (const c of caps) {
    const s = c.sensitivity || 'normal';
    counts[s] = (counts[s] || 0) + 1;
  }

  const total = caps.length;
  const levels = Object.keys(counts);
  const hasElevated = levels.some((l) => l === 'high' || l === 'critical');
  const hasLow = levels.some((l) => l === 'low');

  // Mode 3: omit entirely when no high/critical and no low
  if (!hasElevated && !hasLow) return null;

  // Mode 1: uniform sensitivity
  if (levels.length === 1) {
    return `${total} ${total === 1 ? 'capability' : 'capabilities'} · all ${levels[0]}`;
  }

  // Mode 2: mixed with at least one elevated level.
  // Comma-separated counts in descending severity order.
  // Include 'low' in the breakdown when the line renders.
  const displayLevels = ['critical', 'high', 'normal', 'low'].filter((l) => counts[l] > 0);
  const parts = displayLevels.map((l) => `${counts[l]} ${l}`);
  return `${total} ${total === 1 ? 'capability' : 'capabilities'} · ${parts.join(' · ')}`;
}
